import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import PrimaryPersonCard from 'components/provider/PrimaryPersonCard'
import CompanyDetailsCard from 'components/provider/CompanyDetailsCard'
import AboutCompanyCard from 'components/provider/AboutCompanyCard'
import EditRecommendationCard from 'components/provider/EditRecommendationCard'
import TechnologyCard from 'components/provider/TechnologyCard'
import ProductCard from 'components/provider/ProductCard'
import AdminDetailsCard from 'components/admin/AdminDetailsCard'
import AdminDeliverySettingsCard from 'components/admin/AdminDeliverySettingsCard'
import SmsNotificationsCard from 'components/admin/SmsNotificationsCard'
import SmsFollowupsCard from 'components/admin/SmsFollowupsCard'
import CountiesCard from 'components/provider/CountiesCard'
import CaseStudiesCard from 'components/provider/CaseStudiesCard'
import { productCategoryMap } from 'config/constants/Constants'
function Provider({ provider, brands, updateProviders, onCompanyChange }) {
    //const api = new ProviderAPI()
    const [company, setCompany] = useState(provider)

    useEffect(() => {
        setCompany(provider)
    }, [provider])
    
    function updateCompanyField(path, value) {
        setCompany(prev => {
            const updated = { ...prev }
            const keys = path.split('.')
            let obj = updated
            for (let i = 0; i < keys.length - 1; i++) {
                obj[keys[i]] = { ...obj[keys[i]] }
                obj = obj[keys[i]]
            }
            obj[keys[keys.length - 1]] = value
            return updated
        })
    }

    function updateCompany(updatedCompany) {
        setCompany(updatedCompany)
        // update list of providers in parent with updated version of company
        updateProviders(prev =>
            prev.map(p => (p.companyId === updatedCompany.companyId ? updatedCompany : p))
        )
        // notify ProviderModal so the pinned cards stay in sync
        onCompanyChange && onCompanyChange(updatedCompany)
    }

    const isSolarProvider = company.productsAvailable && company.productsAvailable['Solar']

    // Products this provider offers (admin-controlled via productsAvailable).
    const productsAvailable = company.productsAvailable || {}
    const enabledProducts = Object.keys(productsAvailable).filter(p => productsAvailable[p])
    // Non-solar products with a dedicated technology configuration.
    const productCards = enabledProducts.filter(p => p !== 'Solar' && productCategoryMap[p])

    return (
        <Grid container>
            <Grid item xs={12}>
                    <div id='section-admin-details'>
                        <AdminDetailsCard
                            key={`admin-${company.companyId}`}
                            companyDetails={company}
                            updateField={updateCompanyField}
                            updateCompany={updateCompany}
                        />
                    </div>

                    <div id='section-delivery-settings'>
                        <AdminDeliverySettingsCard
                            key={`delivery-settings-${company.companyId}`}
                            companyDetails={company}
                            updateCompany={updateCompany}
                        />
                    </div>

                    <div id='section-primary-person'>
                        <PrimaryPersonCard
                            key={`primary-${company.companyId}`}
                            companyId={company.companyId}
                            details={company.primaryPerson}
                            companyDetails={company}
                            updateField={updateCompanyField}
                            updateCompany={updateCompany}
                            admin
                        />
                    </div>

                    {enabledProducts.length > 0 && (
                        <SmsNotificationsCard
                            key={`sms-notifications-${company.companyId}`}
                            companyDetails={company}
                            updateField={updateCompanyField}
                            updateCompany={updateCompany}
                        />
                    )}

                    {enabledProducts.length > 0 && (
                        <SmsFollowupsCard
                            key={`sms-${company.companyId}`}
                            companyDetails={company}
                            updateField={updateCompanyField}
                            updateCompany={updateCompany}
                        />
                    )}

                    {enabledProducts.length > 0 && (
                        <div id='section-company-details'>
                            <CompanyDetailsCard
                                key={`details-${company.companyId}`}
                                companyId={company.companyId}
                                details={company.profileDetails}
                                name={company.name}
                                url={company.url}
                                isSolar={isSolarProvider}
                                enabledProducts={enabledProducts}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        </div>
                    )}

                    {enabledProducts.length > 0 && (
                        <div id='section-recommendation'>
                            <EditRecommendationCard
                                key={`recommendation-${company.companyId}`}
                                companyId={company.companyId}
                                details={company.profileDetails || {}}
                                isSolar={!!isSolarProvider}
                                enabledProducts={enabledProducts}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        </div>
                    )}

                    {enabledProducts.length > 0 && (
                        <div id='section-about-company'>
                            <AboutCompanyCard
                                key={`about-${company.companyId}`}
                                companyId={company.companyId}
                                details={company.profileDetailsExtended}
                                name={company.name}
                                url={company.url}
                                counties={company.counties}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        </div>
                    )}

                    <div id='section-counties'>
                        {enabledProducts.length > 0 && (
                            <CountiesCard
                                key={`counties-${company.companyId}`}
                                products={enabledProducts}
                                companyId={company.companyId}
                                companyDetails={company}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        )}
                    </div>

                    {isSolarProvider && (
                        <div id='section-technology'>
                            <TechnologyCard
                                key={`tech-${company.companyId}`}
                                companyId={company.companyId}
                                details={company.technology}
                                companyDetails={company}
                                brands={brands}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        </div>
                    )}

                    {productCards.map(product => (
                        <div id={`section-technology-${product}`} key={`tech-${product}-${company.companyId}`}>
                            <ProductCard
                                product={product}
                                companyId={company.companyId}
                                companyDetails={company}
                                brands={brands}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        </div>
                    ))}

                    {enabledProducts.length > 0 && (
                        <div id='section-case-studies'>
                            <CaseStudiesCard
                                company={company}
                                newProjectDraft={company.newProjectDraft || {}}
                                updateField={updateCompanyField}
                                updateCompany={updateCompany}
                                admin
                            />
                        </div>
                    )}
            </Grid>
        </Grid>
    );
}

export default Provider